import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    DoorClosed, Building2, ChevronDown, Loader2, RefreshCw, Send, AlertTriangle, X,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchClosingTypes,
    fetchClosingCCs,
    fetchPendingCheck,
    submitStoreClosing,
    clearCCList,
    clearPendingCheck,
    clearSubmitResult,
    resetStoreClosing,
    selectSCClosingTypeList,
    selectSCCCList,
    selectSCPendings,
    selectSCBalanceStock,
    selectSCSubmitResult,
    selectSCLoading,
    selectSCErrors,
} from '../../slices/purchaseSlice/storeClosingSlice';

// ── Shared UI (mirrors DailyIssue / ItemsTransfer) ──────────────────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo', action }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
        rose:   'from-rose-50   to-rose-100   dark:from-rose-900/20   dark:to-rose-800/20   border-rose-200   dark:border-rose-700   text-rose-800   dark:text-rose-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <h3 className="font-semibold text-sm">{title}</h3>
                    {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
                </div>
                {action}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

const formatDateForSP = (d) => {
    if (!d) return '';
    if (d instanceof Date) {
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
    }
    return String(d);
};

const addDays = (n) => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    return d;
};

// Legacy IDs 1/3 take a single Close/Reschedule date; 2/4 take a Suspend/Reschedule
// start–end date range. The label text itself is data-driven (StoreCCCloseMaster).
const isSingleDateType = (id) => id === '1' || id === '3';
const isRangeDateType = (id) => id === '2' || id === '4';

const StoreClosing = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userData?.employeeId || userData?.UID || 'system';
    const roleId = userData?.roleId || userData?.RID || '';
    const uid = userData?.UID || userData?.uid || '';

    const closingTypeList = useSelector(selectSCClosingTypeList);
    const ccList            = useSelector(selectSCCCList);
    const pendings           = useSelector(selectSCPendings);
    const balanceStock       = useSelector(selectSCBalanceStock);
    const submitResult       = useSelector(selectSCSubmitResult);
    const loading             = useSelector(selectSCLoading);
    const errors               = useSelector(selectSCErrors);

    const [closingType, setClosingType] = useState('');
    const [selectedCC, setSelectedCC] = useState('');
    const [closeDate, setCloseDate] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [alertNote, setAlertNote] = useState('');
    const [userRemarks, setUserRemarks] = useState('');
    const [showPendingDetails, setShowPendingDetails] = useState(false);

    useEffect(() => {
        dispatch(fetchClosingTypes());
        return () => dispatch(resetStoreClosing());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (submitResult) {
            const label = closingTypeList.find((t) => t.Id === closingType)?.Name || 'Request';
            toast.success(`${label}   Request Submitted Successfully`);
            dispatch(clearSubmitResult());
            handleReset();
        } else if (errors.submit) {
            toast.error(errors.submit);
            dispatch(clearSubmitResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitResult, errors.submit]);

    const dateMode = isSingleDateType(closingType) ? 'single' : isRangeDateType(closingType) ? 'range' : null;
    const hasPending = pendings.length > 0 || balanceStock.length > 0;

    // Auto-opens once a pending check comes back with anything blocking, mirroring
    // the legacy modal that pops up as soon as a cost center with pending items is chosen.
    useEffect(() => {
        if (hasPending) setShowPendingDetails(true);
    }, [hasPending]);

    // ── Closing Type ─────────────────────────────────────────────────────────

    const handleClosingTypeChange = (val) => {
        setClosingType(val);
        setSelectedCC('');
        setCloseDate(''); setStartDate(''); setEndDate('');
        dispatch(clearCCList());
        dispatch(clearPendingCheck());
        setShowPendingDetails(false);
        if (val) dispatch(fetchClosingCCs({ closingType: val, uid, rid: roleId }));
    };

    // ── Cost Center ──────────────────────────────────────────────────────────

    const handleCCChange = (cc) => {
        setSelectedCC(cc);
        dispatch(clearPendingCheck());
        setShowPendingDetails(false);
        if (cc) dispatch(fetchPendingCheck(cc));
    };

    // ── Submit ───────────────────────────────────────────────────────────────

    const handleSubmit = () => {
        if (!closingType) { toast.error('Select Type'); return; }
        if (!selectedCC)  { toast.error('Select CC/Job Code'); return; }
        if (dateMode === 'range') {
            if (!startDate) {
                toast.error(closingType === '2' ? 'Enter Store Suspend Start Date' : 'Enter Store Reschedule Start Date');
                return;
            }
            if (!endDate) {
                toast.error(closingType === '2' ? 'Enter Store Suspend End Date' : 'Enter Store Reschedule End Date');
                return;
            }
        }
        if (dateMode === 'single' && !closeDate) {
            toast.error(closingType === '1' ? 'Enter CC Close Date' : 'Enter CC Reshedule  Date');
            return;
        }
        if (!alertNote.trim()) { toast.error('Enter Alert Note'); return; }
        if (!userRemarks.trim()) { toast.error('Enter Remarks'); return; }
        if (hasPending) {
            setShowPendingDetails(true);
            toast.error('This cost center has pending approvals or stock balance — it cannot be closed.');
            return;
        }

        dispatch(submitStoreClosing({
            ClosingType: closingType,
            ClosingCCs: selectedCC,
            ClosingDate: dateMode === 'single' ? formatDateForSP(closeDate) : '',
            StartDate: dateMode === 'range' ? formatDateForSP(startDate) : '',
            EndDate: dateMode === 'range' ? formatDateForSP(endDate) : '',
            Alertnote: alertNote.trim(),
            UserRemarks: userRemarks.trim(),
            RoleID: String(roleId),
            UserID: String(uid),
            Createdby: currentUser,
        }));
    };

    const handleReset = () => {
        setClosingType(''); setSelectedCC('');
        setCloseDate(''); setStartDate(''); setEndDate('');
        setAlertNote(''); setUserRemarks('');
        setShowPendingDetails(false);
        dispatch(clearCCList());
        dispatch(clearPendingCheck());
    };

    const handleAlertNoteChange = (e) => {
        // Alpha-numeric + spaces only, matching the legacy clsAlphaNoOnly guard
        const cleaned = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '');
        setAlertNote(cleaned.slice(0, 50));
    };

    const endDateMinDate = useMemo(() => (startDate ? new Date(startDate) : undefined), [startDate]);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 space-y-5">

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-600 to-gray-800 flex items-center justify-center shadow">
                        <DoorClosed className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Store Close/Suspend</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Close, suspend, or reschedule a cost center's store.</p>
                    </div>
                </div>
                <button onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>

            {/* ── Type / Cost Center selection ──────────────────────────────── */}
            <SectionCard icon={Building2} title="Request Details" accent="indigo">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label required>Type</Label>
                        <div className="relative">
                            <select className={inputCls} value={closingType} onChange={(e) => handleClosingTypeChange(e.target.value)}
                                disabled={loading.closingTypes}>
                                <option value="">-Please Select-</option>
                                {closingTypeList.map((t) => (
                                    <option key={t.Id} value={t.Id}>{t.Name}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                {loading.closingTypes ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                        </div>
                    </div>
                    {closingType && (
                        <div>
                            <Label required>Store CC/Job Code</Label>
                            <div className="relative">
                                <select className={inputCls} value={selectedCC} onChange={(e) => handleCCChange(e.target.value)} disabled={loading.ccList}>
                                    <option value="">-Please Select-</option>
                                    {ccList.map((cc) => (
                                        <option key={cc.CCId} value={cc.CCId}>{cc.CCName}</option>
                                    ))}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                    {loading.ccList ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </SectionCard>

            {/* ── Pending-check warning ────────────────────────────────────── */}
            {selectedCC && loading.pendingCheck && (
                <div className="flex items-center gap-2 text-sm text-indigo-600"><SpinnerIcon /> Checking pending approvals and stock balance…</div>
            )}
            {selectedCC && hasPending && showPendingDetails && (
                <SectionCard icon={AlertTriangle} title="This Cost Center Cannot Be Closed Yet"
                    subtitle="Resolve the items below before submitting" accent="rose"
                    action={
                        <button onClick={() => setShowPendingDetails(false)} className="text-rose-500 hover:text-rose-700">
                            <X className="w-4 h-4" />
                        </button>
                    }>
                    {pendings.length > 0 && (
                        <div>
                            <p className="text-xs font-bold uppercase tracking-wide mb-1.5">Pending Approvals</p>
                            <div className="overflow-x-auto rounded-lg border border-rose-200 dark:border-rose-700">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="bg-rose-100 dark:bg-rose-900/30">
                                            {['Master Code', 'Pending With', 'Details'].map((h) => (
                                                <th key={h} className="px-2.5 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendings.map((p, idx) => (
                                            <tr key={idx} className="bg-white dark:bg-gray-800">
                                                <td className="px-2.5 py-2 whitespace-nowrap">{p.MasterCode}</td>
                                                <td className="px-2.5 py-2 whitespace-nowrap">{p.PendingRoleName || p.RoleName}</td>
                                                <td className="px-2.5 py-2">{p.PendingDetails}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    {balanceStock.length > 0 && (
                        <div>
                            <p className="text-xs font-bold uppercase tracking-wide mb-1.5">Balance Stock</p>
                            <div className="overflow-x-auto rounded-lg border border-rose-200 dark:border-rose-700">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="bg-rose-100 dark:bg-rose-900/30">
                                            {['Item Code', 'Item Name', 'Specification', 'Quantity'].map((h) => (
                                                <th key={h} className="px-2.5 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {balanceStock.map((b, idx) => (
                                            <tr key={idx} className="bg-white dark:bg-gray-800">
                                                <td className="px-2.5 py-2 whitespace-nowrap font-mono">{b.ItemCode}</td>
                                                <td className="px-2.5 py-2">{b.ItemName}</td>
                                                <td className="px-2.5 py-2">{b.Specification}</td>
                                                <td className="px-2.5 py-2 text-right">{b.Quantity}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </SectionCard>
            )}

            {/* ── Date / remarks / submit ──────────────────────────────────── */}
            {selectedCC && dateMode && (
                <SectionCard icon={DoorClosed} title="Closing Details" accent="amber">
                    {dateMode === 'single' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <Label required>{closingType === '1' ? 'Close Date' : 'Reschedule Date'}</Label>
                                <CustomDatePicker value={closeDate} onChange={(d) => setCloseDate(formatDateForSP(d))}
                                    format="DD-MMM-YYYY" placeholder="Select Date"
                                    minDate={addDays(-1)} maxDate={addDays(-1)} />
                            </div>
                        </div>
                    )}
                    {dateMode === 'range' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <Label required>{closingType === '2' ? 'Suspend Start Date' : 'Reschedule Start Date'}</Label>
                                <CustomDatePicker value={startDate} onChange={(d) => { setStartDate(formatDateForSP(d)); setEndDate(''); }}
                                    format="DD-MMM-YYYY" placeholder="Select Date"
                                    minDate={addDays(15)} maxDate={addDays(30)} />
                            </div>
                            <div>
                                <Label required>{closingType === '2' ? 'Suspend End Date' : 'Reschedule End Date'}</Label>
                                <CustomDatePicker value={endDate} onChange={(d) => setEndDate(formatDateForSP(d))}
                                    format="DD-MMM-YYYY" placeholder="Select Date"
                                    minDate={endDateMinDate} disabled={!startDate} />
                            </div>
                        </div>
                    )}

                    <div>
                        <Label required>Alert Note</Label>
                        <input type="text" className={`${inputCls} uppercase`} maxLength={50}
                            value={alertNote} onChange={handleAlertNoteChange} placeholder="Short note (letters/numbers only)" />
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <Label required>User Remarks</Label>
                            <textarea rows={2} className={inputCls} placeholder="Enter remarks…"
                                value={userRemarks} onChange={(e) => setUserRemarks(e.target.value)} />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button onClick={handleReset}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                            Reset
                        </button>
                        <button onClick={handleSubmit} disabled={loading.submit || hasPending}
                            title={hasPending ? 'Resolve pending approvals/stock balance first' : undefined}
                            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                            {loading.submit ? <SpinnerIcon /> : <Send className="w-4 h-4" />}
                            {loading.submit ? 'Submitting…' : 'Submit'}
                        </button>
                    </div>
                </SectionCard>
            )}
        </div>
    );
};

export default StoreClosing;
